<template>
	<div data-cy="treatment-session-detail">
		<div class="clinical-header">
			<div class="clinical-header-item-left">
				<button
					v-on:click="prev"
					style="padding: 5px"
					:class="{'exit': screen == 0}"
					:aria-label="((screen == 0?$t('close'):$t('previous')))"
				>
					<b-icon icon="x" width="2em" height="2em" v-if="screen == 0"></b-icon>
					<b-icon icon="chevron-left" v-if="screen != 0"></b-icon>
				</button>
			</div>
			<div class="clinical-header-item-left clinical-header-summary" style="width:50px; height: 50px; background-size: 50px auto;">
				<b-icon icon="person-fill" style="width: 50px; height: 50px" v-if="!treatmentsession.student"></b-icon>
				<Avatar :userId="treatmentsession.student" v-if="treatmentsession.student" class="avatar-sq" />
			</div>
            
			<div class="clinical-header-item-left clinical-header-summary pl-1">
				<div>
					{{ $t("id-colon") }} {{ treatmentsession.id }} | {{ $t("student-colon") }}
					{{ treatmentsession.student_displayname }}
					<template v-if="treatmentsession.created > 0 && isTeacher"> | {{ $datetime.getLocaleStringFromTimeStamp(treatmentsession.created, false) }} </template>
				</div>
				<div>
					{{ $t("patient-colon") }} {{ treatmentsession.patient }} |
					{{ $t("specialty-colon") }} {{ treatmentsession.specialtyName }} |
					{{ $t("teacher-colon") }} {{ treatmentsession.teacher_displayname }}
				</div>
			</div>
			<div class="clinical-header-item-right">
				<button
					v-on:click="treatmentsession.isLocked = true; saveTreatmentSession()"
					v-if="showLockSaveBtn"
					:disabled="!canContinue || savingSession"
					:aria-label="($t('lock'))"
				>
					{{ $t("lock") }}
					<b-icon
						icon="check"
						width="1.8em"
						height="1.8em"
						style="vertical-align: middle"
					></b-icon>
				</button>
				<button
					v-on:click="next"
					class="save"
					v-else-if="screen == 2"
					:aria-label="($t('save'))"
                    :disabled="!field_validation"
				>
					{{ $t("save") }}
					<b-icon
						icon="check"
						width="1.8em"
						height="1.8em"
						style="vertical-align: middle"
					></b-icon>
				</button>
				<button
					v-on:click="next"
					v-else-if="screen != 2 && !showLivePatientNotAttendedSaveBtn"
					:disabled="!canContinue"
					:aria-label="($t('next'))"
				>
					{{ $t("next") }}
					<b-icon icon="chevron-right"></b-icon>
				</button>
               
				<button
					v-on:click="saveTreatmentSession()"
					v-else-if="showLivePatientNotAttendedSaveBtn"
					:disabled="!canContinue || savingSession"
				>
					{{ $t("save") }}  
					<b-icon
						icon="check"
						width="1.8em"
						height="1.8em"
						style="vertical-align: middle"
					></b-icon>
				</button>
			</div>
		</div>
		<div class="clinical-content"> 
			<div class="clinical-content-slider">
				<div class="clinical-content-slider-item">
					<div class="container-fluid">
						<div class="row">
							<div class="col-md-4">
								<div class="form-group">
									<label for="student">{{ $t("student") }}</label>
									<input
										id="student"
										:aria-label="($t('student'))"
										type="text"
										v-model="treatmentsession.student_displayname"
										class="form-control"
										:class="{'requiredincomplete': (requiredFields.includes('student') && !treatmentsession.student_displayname)}"
										:disabled="
											treatmentsession.isLocked || !this.canSelectStudent
										"
										@click="selectStudent"
										@keyup.enter="selectStudent"
										ref="student"
									/>
								</div>
								<div class="form-group">
									<label for="teacher">{{ $t("teacher") }}</label>
									<input
										id="teacher"
										:aria-label="($t('teacher'))"
										type="text"
										v-model="treatmentsession.teacher_displayname"
										class="form-control"
										:class="{'requiredincomplete': (requiredFields.includes('teacher') && !treatmentsession.teacher_displayname)}"
										:disabled="treatmentsession.isLocked"
										@click="selectTeacher"
										@keyup.enter="selectTeacher"
										ref="teacher"
									/>
								</div>
								<div class="form-group">
									<label for="specialty">{{ $t("specialty") }}</label>
									<select
										id="specialty"
										:aria-label="($t('specialty'))"
										type="text"
										v-model="treatmentsession.specialtyId"
										class="form-control"
										:class="{'requiredincomplete': (requiredFields.includes('specialtyId') && !treatmentsession.specialtyId)}"
										:disabled="treatmentsession.isLocked"
										ref="specialty"
									>
										<option value="">{{ $t("select-caps") }}</option>
										<option
											v-for="specialty in specialties"
											v-bind:key="specialty.id"
											:value="specialty.id"
											:aria-label="($t('specialty') + ' ' + specialty.name)"
										>
											{{ specialty.name }}
										</option>
									</select>
								</div>
								<div class="form-group">
									<label for="location">{{ $t("location") }}</label>
									<select
										id="location"
										:aria-label="($t('location'))"
										type="text"
										v-model="treatmentsession.clinicId"
										class="form-control"
										:class="{'requiredincomplete': (requiredFields.includes('clinicId') && !treatmentsession.clinicId)}"
										:disabled="treatmentsession.isLocked"
									>
										<option value="">{{ $t("select-caps") }}</option>
										<option
											v-for="clinic in clinics"
											v-bind:key="clinic.id"
											:value="clinic.id"
											:aria-label="($t('location') + ' ' + clinic.name)"
										>
											{{ clinic.name }}
										</option>
									</select>
								</div>
							</div>
							<div class="col-md-4">
								<div class="form-group">
									<label for="treatmentsessiondatetime">{{ $t("date-time") }}</label>
									<input
										id="treatmentsessiondatetime"
										:aria-label="($t('date-time'))"
										type="datetime-local"
										v-model="treatmentsession.datetime"
										class="form-control"
										:class="{'requiredincomplete': (requiredFields.includes('datetime') && !treatmentsession.datetime)}"
										:disabled="treatmentsession.isLocked || this.$config.getValue('tsdisabledateedit')"
									/>
								</div>
								<div class="form-group">
									<label for="sessiontype">{{ $t("session-type") }}</label>
									<select
										id="sessiontype"
										:aria-label="($t('session-type'))"
										type="text"
										v-model="treatmentsession.typeId"
										class="form-control"
										:class="{'requiredincomplete': (requiredFields.includes('typeId') && !treatmentsession.typeId)}"
										:disabled="treatmentsession.isLocked"
										@change="checkParticipationType"
									>
										<option value="">{{ $t("select-caps") }}</option>
										<option
											v-for="participationType in this.$config.getValue(
												'participationTypes'
											)"
											v-bind:key="participationType.id"
											:value="participationType.id"
											:aria-label="($t('session-type') + ' ' + participationType.name)"
										>
											{{ participationType.name }}
										</option>
									</select>
								</div>
							</div>
							<div class="col-md-4">
								<div v-if="participationType && participationType.haspatient == 1">
									<div
										class="form-group"
										v-if="this.$config.getValue('showpatientid')"
									>
										<input
											type="hidden"
											v-model="treatmentsession.clinicalcase.id"
										/>
										<label for="patientid">{{ $t("patient-id") }}</label>
										<input
											id="patientid"
											:aria-label="($t('patient-id'))"
											type="text"
											v-model="treatmentsession.patient"
											class="form-control"
											:class="{'requiredincomplete': (requiredFields.includes('patient') && !treatmentsession.patient&& participationType.haspatient == 1)}"
											:disabled="
												treatmentsession.isLocked ||
													!treatmentsession.student
											"
											@click="trySelectClinicalCase"
											@keyup.enter="trySelectClinicalCase"
										/>
									</div>
								</div>
								<div v-if="participationType && participationType.showattendance == 1 ">
									<div class="form-group">
										<label for="patientattendance">{{ $t("patient-attendance") }}</label>
										<select
											id="patientattendance"
											:aria-label="($t('patient-attendance'))"
											type="text"
											v-model="treatmentsession.patientAttendance"
											class="form-control"
											:disabled="treatmentsession.isLocked"
											:class="{'requiredincomplete': (requiredFields.includes('patientAttendance') && !treatmentsession.patientAttendance && participationType.showattendance == 1)}"
											ref="patientattendance"
										>
											<option :value="null">{{ $t("select-caps") }}</option>
											<option
												v-for="attendance in formsettings.attendanceTypes"
												v-bind:key="attendance.patientattendance_id"
												:value="attendance.patientattendance_id"
												:aria-label="($t('attendance') + ' ' + attendance.patientattendance_title)"
											>
												{{ attendance.patientattendance_title }}
											</option>
										</select>
									</div>
								</div>
								<div
									class="form-group"
									v-if="this.$config.getValue('showchairnumber')"
								>
									<label for="chairnumber">{{ $t("chair-number") }}</label>
									<input
										id="chairnumber"
										:aria-label="($t('chair-number'))"
										type="text"
										v-model="treatmentsession.chairNumber"
										class="form-control"
										:class="{'requiredincomplete': (requiredFields.includes('chairNumber') && !treatmentsession.chairNumber && this.$config.getValue('showchairnumber'))}"
										:disabled="treatmentsession.isLocked"
									/>
								</div>
								<div
									class="form-group"
									v-if="participationType && participationType.haspatient == 1  && treatmentsession.patientAttendance==1 && (treatmentsession.hasPatientFeedback || treatmentsession.feedbacktoken)"
								>
									<label for="feedbacktoken">{{ $t('feedback-token') }}</label>
									<input
										id="feedbacktoken"
										:aria-label="$t('feedback-token')"
										v-if="!treatmentsession.hasPatientFeedback && treatmentsession.feedbacktoken"
										type="text"
										v-model="treatmentsession.feedbacktoken"
										class="form-control"
										:class="{'requiredincomplete': (requiredFields.includes('feedbacktoken') && !treatmentsession.feed && participationType.haspatient == 1)}"
										disabled="true"
									/>
									<h1><div class="badge badge-success block" v-if="treatmentsession.hasPatientFeedback">{{$t('patient-feedback-provided')}}</div></h1>
								</div>

							</div>
						</div>

						<div
							v-for="(competencyTest, competencyTestIndex) in treatmentsession.tests"
							v-bind:key="competencyTestIndex"
							class="row"
						>
							<div class="col-md-12">
								<div class="list-group-item">
									<p class="mb-1 mt-1 font-bold">
										{{ competencyTest.Code }}: {{ competencyTest.Name }}
									</p>
									<p>
										{{ competencyTest.Description }}
									</p>
								</div>
							</div>
						</div>

						<div class="row mt-4">
							<div class="col-md-12">
								<button
									class="btn btn-primary mr-4 mb-2"
									v-if="treatmentsession.teacher && treatmentsession.specialtyId && treatmentsession.clinicId && treatmentsession.typeId"
									data-cy="view-comments-button"
									@click="toggleComments"
								>
									{{ $t("comments") }}
									<b-icon icon="chevron-up"></b-icon>
								</button>

								<button
									class="btn btn-primary mr-4 mb-2"
									v-if="treatmentsession.student && $config.hasModule('tests')"
									data-cy="view-competency-tests-button"
									@click="toggleSelectCompetencyTests"
								>
									{{ $t("tests") }}
									<b-icon icon="chevron-up"></b-icon>
								</button>
							</div>
						</div>
					</div>
				</div>

				<div class="clinical-content-slider-item">
					<template v-if="allowaddprocedures">
						<div class="clinical-procedure-sections">
							<button
								class="btn btn-primary ml-2"
								v-on:click="
									proceduresearch = true;
									procedureselector = [];
								"
								:disabled="treatmentsession.isLocked"
							>
								<b-icon icon="search"></b-icon>
							</button>
							<button
								v-for="section in formsettings.sections"
								v-bind:key="section.id"
								class="btn btn-primary ml-2"
								v-on:click="
									procedureselector = section.procedures;
									proceduresearch = false;
								"
								:disabled="treatmentsession.isLocked"
							>
								{{ section.caption }}
							</button>
						</div>
						<div class="clinical-procedure-selector" v-if="this.procedureselector.length">
							<button class="btn btn-light ml-2 mb-2" v-on:click="procedureselector = []">
								<b-icon icon="caret-up-fill" style="width: 20px; height: 20px"></b-icon>
							</button>
							<button
								v-for="procedurebtn in this.procedureselector"
								v-bind:key="procedurebtn.id"
								class="btn btn-primary ml-2 mb-2"
								v-on:click="addProcedure(procedurebtn)"
								:disabled="treatmentsession.isLocked"
							>
								{{ procedurebtn.caption }}
							</button>
						</div>
						<div class="clinical-procedure-selector" v-if="proceduresearch">
							<button
								class="btn btn-light ml-2 mb-2"
								v-on:click="proceduresearch = false"
							>
								<b-icon icon="caret-up-fill" style="width: 20px; height: 20px"></b-icon>
							</button>
							<input
								type="text"
								class="form-control ml-1"
								style="width: 200px; display: inline-block"
								v-model="proceduresearchstring"
								:placeholder="$t('search-ellipses')"
							/>
							<button
								v-for="procedurebtn in procedureSearchResults()"
								v-bind:key="procedurebtn.id"
								class="btn btn-primary ml-2 mb-2"
								v-on:click="addProcedure(procedurebtn)"
								:disabled="treatmentsession.isLocked"
							>
								{{ procedurebtn.caption }}
							</button>
						</div>
					</template>
					<div class="clinical-procedures" @click="clearProcedureSelect()">
						<div
							v-for="(procedure, index) in treatmentsession.procedures"
							v-bind:key="procedure.treatmentsessionprocedureid"
							class="clinical-procedure-item"
							v-bind:class="{ selected: procedure.isSelected }"
						>
							<div
								class="clinical-procedure-label"
								@click.stop="toggleProcedureSelect(procedure)"
							>
								<h3 style="display: inline;" class="mr-4">{{ procedure.caption }}</h3>
								<b-icon v-if="!procedure.flagged && procedure.incomplete" class="text-danger mr-4" icon="exclamation-triangle"></b-icon>
								<b-icon v-if="procedure.flagged" class="text-danger mr-4" icon="exclamation-triangle-fill"></b-icon>

								<!-- procedure summary here -->
								<TreatmentSessionProcedureSummary
									:treatmentsession="treatmentsession"
									:parentprocedure="procedure"
									:parent="procedure"
									:toothmaplabels="toothmaplabels"
									:primarylabels="primarylabels"
									v-if="!anyProcedureSelected"
									class="d-none d-sm-inline"
								/>
							</div>

							<div class="clinical-procedure-detail" @click.stop="">
								<h2 class="clearfix mb-2">
									{{ procedure.caption }}
									<span class="float-right">
										<button
											class="btn btn-outline-secondary btn-sm ml-1"
											v-if="procedure.history"
											v-on:click="showProcedureHistory(procedure)"
										>
											{{procedure.history.sessions.length}}
										</button>
										<button
											class="btn btn-outline-secondary btn-sm ml-1"
											v-if="procedure.information || procedure.validationinformation"
											v-on:click="
												showProcedureInfo(procedure)
											"
										>
											<b-icon icon="question"></b-icon>
										</button>
										<button
											class="btn btn-outline-secondary btn-sm ml-1"
											v-on:click="removeProcedure(index, procedure)"
										>
											<b-icon icon="trash"></b-icon>
										</button>
									</span>
								</h2>
								<TreatmentSessionFormElements
									v-bind:treatmentsession="treatmentsession"
									v-bind:parentprocedure="procedure"
									v-bind:parent="procedure"
									v-bind:toothchartnotationoptions="toothchartnotationoptions"
									v-bind:toothchartnotationstyle="toothchartnotationstyle"
									v-bind:toothchartnotationswitchable="
										toothchartnotationswitchable
									"
									v-bind:toothmaplabels="toothmaplabels"
									v-bind:primarylabels="primarylabels"
								></TreatmentSessionFormElements>
							</div>
						</div>
					</div>
				</div>

				<div class="clinical-content-slider-item">
					<div class="container-fluid">
						<div class="row" v-if="this.$config.getValue('trackclinicalcases') && treatmentsession.patient">
							<div class="col-md-12">
								<div
									class="card mt-3 mb-3 no-border border-radius-2"
									v-if="treatmentsession.assessment.markingmain"
								>
									<div class="card-body">
										<div class="form-group">
											<label>{{
												$t("is-the-planned-treatment-complete-question")
											}}</label>
											<div
												class="btn-group btn-group-vertical-sm"
												style="display: block"
											>
												<button
													class="btn"
													v-bind:class="{
														'btn-on':
															treatmentsession.clinicalcase
																.complete == -1,
														'btn-off':
															treatmentsession.clinicalcase
																.complete != -1
													}"
													v-on:click="
														treatmentsession.clinicalcase.complete = -1
													"
													:disabled="treatmentsession.isLocked"
												>
													{{ $t("na") }}
												</button>
												<button
													class="btn"
													v-bind:class="{
														'btn-on':
															treatmentsession.clinicalcase
																.complete == 0,
														'btn-off':
															treatmentsession.clinicalcase
																.complete != 0
													}"
													v-on:click="
														treatmentsession.clinicalcase.complete = 0
													"
													:disabled="treatmentsession.isLocked"
												>
													{{ $t("incomplete") }}
												</button>
												<button
													class="btn"
													v-bind:class="{
														'btn-on':
															treatmentsession.clinicalcase
																.complete == 1,
														'btn-off':
															treatmentsession.clinicalcase
																.complete != 1
													}"
													v-on:click="
														treatmentsession.clinicalcase.complete = 1
													"
													:disabled="treatmentsession.isLocked"
												>
													{{ $t("complete") }}
												</button>
											</div>
										</div>
										<div v-if="treatmentsession.clinicalcase.complete == 1">
											<div class="form-group">
												<div
													class="btn-group btn-group-vertical-sm"
													style="display: block"
												>
													<button
														v-for="completetype in this.$config.getValue(
															'clinicalcasecompletetypes'
														)"
														v-bind:key="completetype.value"
														class="btn"
														v-bind:class="{
															'btn-on':
																treatmentsession.clinicalcase
																	.status == completetype.value,
															'btn-off':
																treatmentsession.clinicalcase
																	.status != completetype.value
														}"
														v-on:click="
															treatmentsession.clinicalcase.status =
																completetype.value
														"
														:disabled="treatmentsession.isLocked"
													>
														{{ completetype.caption }}
													</button>
												</div>
											</div>
											<div
												class="form-group"
												v-if="
													treatmentsession.clinicalcase.status == 'recall'
												"
											>
												<div
													class="btn-group btn-group-vertical-sm"
													style="display: block"
												>
													<button
														v-for="recallperiod in this.$config.getValue(
															'clinicalcaserecalloptions'
														)"
														v-bind:key="recallperiod.value"
														class="btn"
														v-bind:class="{
															'btn-on':
																treatmentsession.clinicalcase
																	.recallperiod ==
																recallperiod.value,
															'btn-off':
																treatmentsession.clinicalcase
																	.recallperiod !=
																recallperiod.value
														}"
														v-on:click="
															treatmentsession.clinicalcase.recallperiod =
																recallperiod.value
														"
														:disabled="treatmentsession.isLocked"
													>
														{{ recallperiod.caption }}
													</button>
												</div>
											</div>
											<div
												v-if="
													treatmentsession.clinicalcase.status ==
														'transferred'
												"
											>
												<div class="form-group">
													<div
														class="btn-group btn-group-vertical-sm"
														style="display: block"
													>
														<button
															v-for="transfer in this.$config.getValue(
																'clinicalcasetransferoptions'
															)"
															v-bind:key="transfer.value"
															class="btn"
															v-bind:class="{
																'btn-on':
																	treatmentsession.clinicalcase
																		.transferred ==
																	transfer.value,
																'btn-off':
																	treatmentsession.clinicalcase
																		.transferred !=
																	transfer.value
															}"
															v-on:click="
																treatmentsession.clinicalcase.transferred =
																	transfer.value
															"
															:disabled="treatmentsession.isLocked"
														>
															{{ transfer.caption }}
														</button>
													</div>
												</div>
												<div
													class="form-group"
													v-if="
														treatmentsession.clinicalcase.transferred ==
															'Another Student' ||
															treatmentsession.clinicalcase
																.transferred == 'Staff'
													"
												>
													<div class="form-group">
														<label
															v-if="
																treatmentsession.clinicalcase
																	.transferred ==
																	'Another Student'
															"
															>{{$t('student')}}</label
														>
														<label
															v-if="
																treatmentsession.clinicalcase
																	.transferred == 'Staff'
															"
															>{{$t('staff-member')}}</label
														>
														<input
															type="text"
															v-model="
																treatmentsession.clinicalcase
																	.transferredtouserDisplayName
															"
															class="form-control"
															:disabled="treatmentsession.isLocked"
															@focus="selectTransferredTo"
														/>
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>

						<div class="row">
							<div
								v-for="procedure in treatmentsession.procedures"
								v-bind:key="procedure.treatmentsessionprocedureid"
								class="col-md-12"
							>
								<div
									class="card mb-3 no-border border-radius-2"
									v-if="procedure.marking.formElements.length"
								>
									<div class="card-body">
										<div class="mb-2">
											<h3 class="mb-0">{{ procedure.caption }}</h3>
											<!-- procedure summary here -->
											<TreatmentSessionProcedureSummary
												:treatmentsession="treatmentsession"
												:parentprocedure="procedure"
												:parent="procedure"
												:toothmaplabels="toothmaplabels"
												:primarylabels="primarylabels"
												class="d-none d-sm-inline"
											/>
										</div>
										<!-- Marking -->
										<TreatmentSessionMarkingElements
											:treatmentsession="treatmentsession"
											:parent="procedure.marking"
											:canMark="canMark"
											@show-form-response="handleShowFormResponse"
											@show-form-fill="handleShowFormFill"
										></TreatmentSessionMarkingElements>
									</div>
								</div>
							</div>
						</div>

						<div class="row">
							<div class="col-md-6">
								<div
									class="card mb-3 no-border border-radius-2"
									v-if="
										treatmentsession.tests !== undefined &&
											treatmentsession.tests.length
									"
								>
									<div class="card-body">
										<div
											v-for="(test, testIndex) in treatmentsession.tests"
											v-bind:key="testIndex"
										>
											<TreatmentSessionCompetencyTestMarking
												:treatmentsession="treatmentsession"
												:competencytest="test"
												:canMark="canMark"
												@markCompetencyTest="markCompetencyTest"
											></TreatmentSessionCompetencyTestMarking>
										</div>
									</div>
								</div>

								<div
									class="card mb-3 no-border border-radius-2"
									v-if="
										treatmentsession.assessment.markingmain &&
											treatmentsession.assessment.markingmain.display
									"
								>
									<div class="card-body">
										<TreatmentSessionMarkingElements
											:treatmentsession="treatmentsession"
											:parent="treatmentsession.assessment.markingmain"
											:canMark="canMark"
											@validation="validationHandler"
											@show-form-response="handleShowFormResponse"
											@show-form-fill="handleShowFormFill"
										></TreatmentSessionMarkingElements>
									</div>
								</div>

								<div
									class="card mb-3 no-border border-radius-2"
									v-if="
										treatmentsession.assessment.markingsummary &&
											treatmentsession.assessment.markingsummary.display
									"
								>
									<div class="card-body">
										<TreatmentSessionMarkingElements
											:treatmentsession="treatmentsession"
											:parent="treatmentsession.assessment.markingsummary"
											:canMark="canMark"
											@validation="validationHandler"
											@show-form-response="handleShowFormResponse"
											@show-form-fill="handleShowFormFill"
										></TreatmentSessionMarkingElements>
									</div>
								</div>
							</div>
							<div class="col-md-6">
								<div
									class="card mb-3 no-border border-radius-2"
									v-if="
										treatmentsession.assessment.markingsupp &&
											treatmentsession.assessment.markingsupp.display
									"
								>
									<div class="card-body">
										<TreatmentSessionMarkingElements
											:treatmentsession="treatmentsession"
											:parent="treatmentsession.assessment.markingsupp"
											:canMark="canMark"
											@validation="validationHandler"
											@show-form-response="handleShowFormResponse"
											@show-form-fill="handleShowFormFill"
										></TreatmentSessionMarkingElements>
									</div>
								</div>

								<div class="card mb-3 no-border border-radius-2">
									<!-- Lock control -->
									<div class="card-body">
										<div class="form-group mb-0">
											<div class="btn-group" style="display: block">
												<button
													class="btn"
													v-bind:class="{
														'btn-danger':
															treatmentsession.isLocked == true,
														'btn-light':
															treatmentsession.isLocked != true
													}"
													v-on:click="treatmentsession.isLocked = true"
													:disabled="!canLock"
												>
													{{ $t("locked") }}
												</button>
												<button
													class="btn"
													v-bind:class="{
														'btn-primary':
															treatmentsession.isLocked != true,
														'btn-light':
															treatmentsession.isLocked == true
													}"
													v-on:click="treatmentsession.isLocked = false"
													:disabled="!canLock"
												>
													{{ $t("unlocked") }}
												</button>
											</div>
										</div>
									</div>
								</div>


								<div class="card mb-3 no-border border-radius-2" v-if="treatmentsession.cansubmitstudentfeedback">
									<!-- Lock control -->
									<div class="card-body">
										<div class="form-group mb-0">
											<button
												class="btn btn-primary mr-4 mb-2"
												data-cy="view-competency-tests-button"
												@click="goToStudentFeedback()"
											>
												{{ $t("feedback-for-teacher") }}
											</button>
										</div>
									</div>
								</div>


								<template v-if="!isTeacher && !$config.hasModule('shibboleth')">
									<div class="card mb-3 no-border border-radius-2">
										<div class="card-body">
											<template v-if="!markedByUser">
												<p><strong>{{$t('teacher-login')}}</strong></p>
												<div class="alert alert-danger" v-if="loginFailed">{{ $t("login-failed") }}</div>
												<div class="form-group">
													<input class="form-control" type="text" v-model="tryMarkedByUser" :placeholder="$t('username')" autocomplete="new-password" autocorrect="off" autocapitalize="off">
												</div>
												<PasswordInput
													v-model="tryMarkedByPassword"
													:label="$t('password')"
													sr-only
													:placeholder="$t('password')"
													autocomplete="new-password"
													@enter="tryLogin()"
												/>
												<div class="form-group">
													<button
														class="btn btn-primary float-right"
														v-on:click="tryLogin()"
													>
														{{ $t("login") }}
													</button>
												</div>
											</template>
											<template v-else>
												<div class="form-group">
													<button
														class="btn btn-primary float-right"
														v-on:click="logout()"
													>
														{{ $t("teacher-logout") }}
													</button>
												</div>
											</template>
										</div>
									</div>
								</template>

							</div>
						</div>
					</div>
				</div>
			</div>
		</div>

		<TreatmentSessionComments v-bind:treatmentsession="treatmentsession">
		</TreatmentSessionComments>

		<TreatmentSessionSelectCompetencyTests v-bind:treatmentsession="treatmentsession">
		</TreatmentSessionSelectCompetencyTests>

		<TreatmentSessionCompetencyTestMarkingForm
			:competencytestmarking="competencytestmarking"
			:showcompetencytestmarking="showcompetencytestmarking"
			:readonly="!canMark"
			:canmark="canMark"
            :submittercanedittheirfields="submittercanedittheirfields"
			@hideCompetencyTestMarking="hideCompetencyTestMarking"
		>
		</TreatmentSessionCompetencyTestMarkingForm>

		<div class="modal-mask"  v-if="procedurehistory">
			<div class="modal-wrapper">
				<div class="modal-dialog modal-lg">
					<div class="modal-content">
						<div class="modal-header">
							<h5 class="modal-title">{{ $t("procedure-history") }}</h5>
							<button class="btn" @click="procedurehistory=null">
								<b-icon icon="x"></b-icon>
							</button>
						</div>
						<div class="modal-body">
							<h3>{{ $t("grades") }}</h3>
						 	<template v-for="(grade, index) in procedurehistory.grades">
						 		<p v-bind:key="index" class="mb-0"><strong>{{grade.caption}}</strong></p>
								<table class="table table-sm table-bordered" v-bind:key="index">
									<tr>
										<th v-for="(grade, index) in grade.values" :key="index">
											{{grade.caption}}
										</th>
									</tr>
									<tr>
										<td v-for="(grade, index) in grade.values" :key="index">
											{{grade.total}}
										</td>
									</tr>
								</table>
							</template>
							<h3>{{ $t("treatment-sessions") }}</h3>
						 	<template v-for="(session, index) in procedurehistory.sessions">
						 		<p v-bind:key="index" class="mb-0"><strong>{{$datetime.getLocaleStringFromTimeStamp(session.DateOfTreatment)}}</strong></p>
						 		<p v-bind:key="index" class="mb-0">{{session.clinic}}</p>
								<table class="table table-sm table-bordered" v-bind:key="index">
									<tr>
										<template v-for="(grade, index) in procedurehistory.grades">
											<th v-if="session[grade.id]" :key="index">
												{{grade.caption}}
											</th>
										</template>
									</tr>
									<tr>
										<template v-for="(grade, index) in procedurehistory.grades">
											<th v-if="session[grade.id]" :key="index">
												{{session[grade.id]}}
											</th>
										</template>
									</tr>
								</table>
							</template>
						</div>
						<div class="modal-footer">
							<button
								type="button"
								class="btn btn-secondary"
								@click="procedurehistory=null"
								data-cy="dialog-confirm"
							>
								{{ $t("ok") }}
							</button>
						</div>
					</div>
				</div>
			</div>
		</div>
	<FormFillModal
		v-if="formFillModal.show"
		:form-id="formFillModal.formId"
		@submitted="confirmFormFillModal"
		@cancel="cancelFormFillModal"
	/>

	<div class="modal-mask" v-if="formResponseModal.show">
		<div class="modal-wrapper">
			<div class="modal-dialog modal-lg">
				<div class="modal-content">
					<div class="modal-header">
						<h5 class="modal-title">{{ $t("form") }}</h5>
						<button class="btn" @click="cancelFormResponseModal">
							<b-icon icon="x"></b-icon>
						</button>
					</div>
					<div class="modal-body">
						<FormResponseViewer :formresponse-id="formResponseModal.formresponseId" />
					</div>
					<div class="modal-footer">
						<button type="button" class="btn btn-secondary" @click="cancelFormResponseModal">
							{{ $t("cancel") }}
						</button>
						<button type="button" class="btn btn-primary" @click="confirmFormResponseModal">
							{{ $t("confirm") }}
						</button>
					</div>
				</div>
			</div>
		</div>
	</div>
	</div>
</template>

<script>
import TreatmentSessionFormElements from "src/components/TreatmentSessionFormElements.vue";
import TreatmentSessionMarkingElements from "src/components/TreatmentSessionMarkingElements.vue";
import FormResponseViewer from "src/components/FormResponseViewer.vue";
import FormFillModal from "src/components/FormFillModal.vue";
import TreatmentSessionComments from "src/components/TreatmentSessionComments.vue";
import TreatmentSessionSelectCompetencyTests from "src/components/TreatmentSessionSelectCompetencyTests.vue";
import TreatmentSessionCompetencyTestMarking from "src/components/TreatmentSessionCompetencyTestMarking.vue";
import TreatmentSessionCompetencyTestMarkingForm from "src/components/TreatmentSessionCompetencyTestMarkingForm.vue";
import TreatmentSessionProcedureSummary from "src/components/TreatmentSessionProcedureSummary.vue";
import Avatar from "src/components/Avatar.vue";

export default {
	props: {
		treatmentsessionformelements: Object,
		treatmentsessionmarkingelements: Object
	},
	components: {
		TreatmentSessionFormElements,
		TreatmentSessionMarkingElements,
		TreatmentSessionComments,
		TreatmentSessionSelectCompetencyTests,
		TreatmentSessionCompetencyTestMarking,
		TreatmentSessionCompetencyTestMarkingForm,
		TreatmentSessionProcedureSummary,
		Avatar,
		FormResponseViewer,
		FormFillModal
	},
	data() {
		return {
			updating: false,
			savingSession: false,
			numupdates: 0,
			screen: 0,
			formsettings: {},
			procedureselector: {},
			proceduresearch: false,
			proceduresearchstring: "",
			procedurehistory: null,
			commentsModalIsOpen: false,
			allowaddprocedures: true,
			loginFailed: false,
			tryMarkedByUser: null,
			tryMarkedByPassword: null,
			markedByUser: null,
			markedByPassword: null,
			treatmentsession: {
				id: null,
				student: null,
				student_displayname: null,
				teacher: null,
				teacher_displayname: null,
				patient: null,
				isLocked: false,
				procedures: [],
				tests: [],
				assessment: {},
				grade: {},
				oknotok: {},
				textarea: {},
				bool: {},
				datetime: this.$datetime.getDateTimeLocalFieldNowValue(),
				flagged: false,
				typeId: null,
				clinicId: null,
				clinicName: "",
				specialtyId: null,
				specialtyName: "",
				patientAttendance: null,
				chairNumber: null,
				clinicalcase: {
					id: null,
					patientref: null,
					complete: false,
					status: null,
					recall: null,
					recallperiod: null,
					transferred: null,
					transferredtouser: null,
					transferredtouserDisplayName: null
				},
				feedbacktoken: null,
				created: 0
			},
			dependencies: {},
			toothchartnotationoptions: {
				fdi: "FDI",
				usa: "USA",
				palmer: "Palmer"
			},
			toothchartnotationstyle: "fdi",
			toothchartnotationswitchable: true,
			toothmaplabels: [
				{},
				{
					usa: "1",
					fdi: "18",
					palmer: "8"
				},
				{
					usa: "2",
					fdi: "17",
					palmer: "7"
				},
				{
					usa: "3",
					fdi: "16",
					palmer: "6"
				},
				{
					usa: "4",
					fdi: "15",
					palmer: "5"
				},
				{
					usa: "5",
					fdi: "14",
					palmer: "4"
				},
				{
					usa: "6",
					fdi: "13",
					palmer: "3"
				},
				{
					usa: "7",
					fdi: "12",
					palmer: "2"
				},
				{
					usa: "8",
					fdi: "11",
					palmer: "1"
				},
				{
					usa: "9",
					fdi: "21",
					palmer: "1"
				},
				{
					usa: "10",
					fdi: "22",
					palmer: "2"
				},
				{
					usa: "11",
					fdi: "23",
					palmer: "3"
				},
				{
					usa: "12",
					fdi: "24",
					palmer: "4"
				},
				{
					usa: "13",
					fdi: "25",
					palmer: "5"
				},
				{
					usa: "14",
					fdi: "26",
					palmer: "6"
				},
				{
					usa: "15",
					fdi: "27",
					palmer: "7"
				},
				{
					usa: "16",
					fdi: "28",
					palmer: "8"
				},
				{
					usa: "17",
					fdi: "38",
					palmer: "8"
				},
				{
					usa: "18",
					fdi: "37",
					palmer: "7"
				},
				{
					usa: "19",
					fdi: "36",
					palmer: "6"
				},
				{
					usa: "20",
					fdi: "35",
					palmer: "5"
				},
				{
					usa: "21",
					fdi: "34",
					palmer: "4"
				},
				{
					usa: "22",
					fdi: "33",
					palmer: "3"
				},
				{
					usa: "23",
					fdi: "32",
					palmer: "2"
				},
				{
					usa: "24",
					fdi: "31",
					palmer: "1"
				},
				{
					usa: "25",
					fdi: "41",
					palmer: "1"
				},
				{
					usa: "26",
					fdi: "42",
					palmer: "2"
				},
				{
					usa: "27",
					fdi: "43",
					palmer: "3"
				},
				{
					usa: "28",
					fdi: "44",
					palmer: "4"
				},
				{
					usa: "29",
					fdi: "45",
					palmer: "5"
				},
				{
					usa: "30",
					fdi: "46",
					palmer: "6"
				},
				{
					usa: "31",
					fdi: "47",
					palmer: "7"
				},
				{
					usa: "32",
					fdi: "48",
					palmer: "8"
				}
			],
			primarylabels: [
				{},
				{
					usa: "A",
					fdi: "55",
					palmer: "E"
				},
				{
					usa: "B",
					fdi: "54",
					palmer: "D"
				},
				{
					usa: "C",
					fdi: "53",
					palmer: "C"
				},
				{
					usa: "D",
					fdi: "52",
					palmer: "B"
				},
				{
					usa: "E",
					fdi: "51",
					palmer: "A"
				},
				{
					usa: "F",
					fdi: "61",
					palmer: "A"
				},
				{
					usa: "G",
					fdi: "62",
					palmer: "B"
				},
				{
					usa: "H",
					fdi: "63",
					palmer: "C"
				},
				{
					usa: "I",
					fdi: "64",
					palmer: "D"
				},
				{
					usa: "J",
					fdi: "65",
					palmer: "E"
				},
				{
					usa: "K",
					fdi: "75",
					palmer: "E"
				},
				{
					usa: "L",
					fdi: "74",
					palmer: "D"
				},
				{
					usa: "M",
					fdi: "73",
					palmer: "C"
				},
				{
					usa: "N",
					fdi: "72",
					palmer: "B"
				},
				{
					usa: "O",
					fdi: "71",
					palmer: "A"
				},
				{
					usa: "P",
					fdi: "81",
					palmer: "A"
				},
				{
					usa: "Q",
					fdi: "82",
					palmer: "B"
				},
				{
					usa: "R",
					fdi: "83",
					palmer: "C"
				},
				{
					usa: "S",
					fdi: "84",
					palmer: "D"
				},
				{
					usa: "T",
					fdi: "85",
					palmer: "E"
				}
			],
			showcompetencytestmarking: false,
			competencytestmarking: {},
            submittercanedittheirfields : false,
            validatedproceduresmandatory: false,
            mandatoryprocedures: [],
            field_validation: true,
            formResponseModal: { show: false, formresponseId: null, formElement: null, value: null },
            formFillModal: { show: false, formId: null, formElement: null, value: null }
		};
	},
	created() {
  		window.addEventListener("resize", this.resizeHandler);
	},
	mounted() {
		this.snapListen();
		this.getFormSettings().then(() => {
			this.getTreatmentSession();
		});
	},
	computed: {
		requiredFields : function (){
			return this.$config.getValue('tsrequiredfields');
		},
		anyProcedureSelected() {
			for(let procedure of this.treatmentsession.procedures) {
				if(procedure.isSelected) {
					return true;
				}
			}
			return false;
		},
		avatarStyle() {

			const url = `${this.$settings.APIBaseURL}/asset/avatar/${this.treatmentsession.student}?username=${this.$UserSession.getValue("username")}&authToken=${this.$UserSession.getValue("authToken")}`;

			return `background-image: url(${url})`;
		},
		specialties: function() {
			return this.$config.getValue("specialties");
		},
		clinics: function() {
			return this.$config.getValue("clinics");
		},
		canSelectStudent: function() {
			return this.$UserSession.hasPriv.call(this, "MARK_TREATMENTSESSIONS");
		},
		isTeacher: function() {
			return this.$UserSession.hasPriv.call(this, "MARK_TREATMENTSESSIONS");
		},
		canContinue() {
			//return false;
			var validated = true;
			for (let i = 0; i < this.requiredFields.length; i++) {
				let field = this.requiredFields[i];
				if (field === 'patientAttendance') {
					// Skip this field if a certain condition is met
					if (this.participationType && this.participationType.showattendance == 0) {
						continue;
					}
				}
				if (field === 'patient') {
					// Skip this field if a certain condition is met
					if (this.participationType && this.participationType.haspatient == 0) {
						continue;
					}
				}
				if(!(field in this.treatmentsession) || !this.treatmentsession[field]){
					validated = false;
				}
			}
            let condition = (
				this.screen || //already past first screen // or
				validated // first screen validated
			);

            if(this.anyProcedureSelected && this.validatedproceduresmandatory){
                const mandatory = this.treatmentsession.procedures
                    .filter(proc => proc.validation && proc.validation.length > 0 && proc.incomplete)

                condition = mandatory.length > 0 ? false : condition
            }
			return condition
		},
		participationType() {
			var selectedParticipationType = null;
			this.$config.getValue('participationTypes').forEach(participationType => {
				if(participationType.id == this.treatmentsession.typeId) {
					selectedParticipationType = participationType;
				}
			});

			return selectedParticipationType;

		},
		showLivePatientNotAttendedSaveBtn() {
			return (
				this.participationType
				&& this.participationType.showattendance == 1 // show attendance option
				&& this.treatmentsession.patientAttendance != 1 // attendance not selected
				&& (!this.$config.getValue('allowpatientdnamarking') || this.treatmentsession.patientAttendance == null)
			);
		},
		showLockSaveBtn() {
			return (
				this.isTeacher
				&& this.participationType
				&& this.participationType.showattendance == 1 // show attendance option
				&& this.treatmentsession.patientAttendance
				&& this.treatmentsession.patientAttendance != 1
				&& (!this.$config.getValue('allowpatientdnamarking') || this.treatmentsession.patientAttendance == null)
			);
		},
		patientDidNotAttend() {
			return (
				this.participationType
				&& this.participationType.showattendance == 1
				&& this.treatmentsession.patientAttendance != 1 // did not attend
			)
		},
		canMark: function(){
			if(this.treatmentsession.isLocked){
				return false;
			}
			if(this.markedByUser && this.markedByPassword){
				return true;
			}
			return this.$UserSession.hasPriv.call(this, 'MARK_TREATMENTSESSIONS');
		},
		canLock: function(){
			if(this.markedByUser && this.markedByPassword){
				return true;
			}
			return this.$UserSession.hasPriv.call(this, 'MARK_TREATMENTSESSIONS');
		}
	},
	watch: {
		treatmentsession: {
			handler() {
				//this.$log.info.call(this, "change detected");
				// Prevent a race condition of infinite watcher loops
				if (!this.updating) {
					this.updating = true;
				}
			},
			deep: true
		},
		updating: {
			handler() {
				if (this.updating) {
					//this.$log.info.call(this, "updating");
					this.validateDependencies();
					this.checkRequired();
					this.tryGetFeedbackToken();
					this.numupdates++;
					//this.numupdates++;
				}
			},
			deep: true
		},
		numupdates: {
			handler() {
				this.updating = false;
			},
			deep: true
		},
        'treatmentsession.datetime': function(newVal, oldVal) {
           
            if (newVal !== oldVal 
                && oldVal !== this.$datetime.getDateTimeLocalFieldNowValue()
            ) {
                //const self = this;
                this.$store.commit("openDialog", {
                    title: this.$t("are-you-sure"),
                    content: this.$t("you-are-changing-a-datetime")
                });
            }
        }
	},
	methods: {
		unableToGetFormSettings() {
			const self = this;
			this.$store.commit("openDialog", {
				title: this.$t("oops"),
				content: this.$t("unable-to-get-form-settings"),
				confirmCallBack: function() {
					self.$router.replace("/clinical");
				}
			});
		},
		goToStudentFeedback() {
			//this.$store.commit("showBlockingSpinner");
			//this.$router.push(`/clinical/studentfeedback/${item.id}`);
			if(this.treatmentsession.isLocked){
				this.$router.push(`/clinical/studentfeedback/${this.treatmentsession.id}`);
			} else {
				var self = this;
				this.$store.commit("openDialog", {
					title: this.$t("save-first-q"),
					content: this.$t("do-you-want-to-save-the-treatment-session-first-question"),
					cancelBtn: true,
					cancelBtnText: this.$t("no"),
					confirmBtnText: this.$t("yes"),
					confirmCallBack: function() {
						self.saveTreatmentSession(function(){
							self.$router.push(`/clinical/studentfeedback/${self.treatmentsession.id}`);
						});
					},
					cancelCallBack: function() {
						this.$router.push(`/clinical/studentfeedback/${self.treatmentsession.id}`);
					}
				});
			}
		},
		getFormSettings() {
			this.toothchartnotationoptions = this.$config.getValue('toothchartnotationoptions');
			this.toothchartnotationstyle = this.$config.getValue('toothchartnotationstyle');
			if(this.$config.getValue('toothchartnotationswitchable') == 0){
				this.toothchartnotationswitchable = false;
			}
			var promise = this.$cfetch(`${this.$settings.APIBaseURL}/getclinicalsettings`, {
				cache: true,
				method: "post"
			})
				.then(json => {
					if (json.status === "SUCCESS") {
						//this.$log.info.call(this, "Form settings loaded");
						// Tidy up any missing data required for Ui rendering
						json.sections.forEach(section => {
							section.procedures.forEach(item => {
								item.isSelected = false;
								/*
							item.marking = {
								formElements: []
							};
							*/
								this.iterateFormElements(item.formElements);
							});
						});
						this.formsettings = json;
                        this.validatedproceduresmandatory = json.validatedproceduresmandatory

                        this.mandatoryprocedures = this.formsettings.sections.flatMap(section => section.procedures) // Flatten the procedures arrays
                        .filter(procedure => procedure.validation && procedure.validation.length > 0);

					} else {
						this.unableToGetFormSettings();
						this.$log.error.call(this, "Could not fetch form settings");
					}
				})
				.catch(error => {
					console.error("getFormSettings failed:", error);
					this.unableToGetFormSettings();
					this.$log.error.call(this, error);
				});
			return promise;
		},
		getTreatmentSession() {
			if (this.$route.params.treatmentsessionid) {
				this.$cfetch(`${this.$settings.APIBaseURL}/treatmentsessions/load`, {
					method: "post",
					data: {
						id: this.$route.params.treatmentsessionid
					}
				})
					.then(json => {
						this.$store.commit("hideBlockingSpinner");
						if (json.status === "SUCCESS") {
							//this.$log.info.call(this, "Treatment session loaded");
							// Tidy up any missing data required for Ui rendering
							if(!('procedures' in json.treatmentsession)){
								json.treatmentsession.procedures = []
							}
							json.treatmentsession.procedures.forEach(item => {
								item.isSelected = false;
								item.incomplete = false;
								item.flagged = false;
								// Add the history
								item.history = null;
								this.getProcedureHistory(item, json.treatmentsession.student);
								this.iterateFormElements(item.formElements, item);
							});
							json.treatmentsession.assessment = {};
							// Fix the date for the UI
							json.treatmentsession.datetime = this.$datetime.getDateTimeFieldValueFromTimestamp(
								json.treatmentsession.treatmentTimestamp
							);
							json.treatmentsession.clinicalcase.transferredtouserDisplayName = "";
							// Fix clinicalcase
							if (json.treatmentsession.clinicalcase.transferredtouser) {
								this.$users.getDisplayName
									.call(
										this,
										json.treatmentsession.clinicalcase.transferredtouser
									)
									.then(result => {
										json.treatmentsession.clinicalcase.transferredtouserDisplayName = result;
									});
							}
							this.treatmentsession = json.treatmentsession;
							// Configure the marking for the UI
							this.addMarking();

							if(this.$route.query.opencomments) {
								this.toggleComments();
							}
							if(this.$route.params.path1 == 'marking'){
								this.next();
								this.next();
							}
						} else {
							this.unableToOpenTreatmentSession();
							this.$log.error.call(this, "Could not fetch treatment session");
						}
					})
					.catch(error => {
						this.$store.commit("hideBlockingSpinner");
						this.$log.error.call(this, error);
						this.unableToOpenTreatmentSession();
					});
			} else {
				// No treatmentsessionid defined. This is a new session.
				if (!this.canSelectStudent) {
					this.treatmentsession.student = this.$UserSession.getValue("id");
					this.treatmentsession.student_displayname = this.$UserSession.getValue(
						"displayName"
					);
				}
				if (this.isTeacher) {
					this.treatmentsession.teacher = this.$UserSession.getValue("id");
					this.treatmentsession.teacher_displayname = this.$UserSession.getValue(
						"displayName"
					);
				}
				this.addMarking();
			}
		},
		unableToOpenTreatmentSession() {
			const self = this;
			this.$store.commit("openDialog", {
				title: this.$t("oops"),
				content: this.$t("unable-to-open-treatment-session"),
				confirmCallBack: function() {
					self.$router.replace("/clinical");
				}
			});
		},
		trySaveTreatmentSession() {
			var self = this;
		    if(this.isTeacher) {
            
                
				if(this.$config.getValue('markingvalidation') == 'autolock'){

					var hasMarkedSomething = false;
					var hasntMarkedSomething = false;
        
					if(!this.treatmentsession.isLocked){
						for (let section in this.treatmentsession.assessment) {
							if('formElements' in this.treatmentsession.assessment[section]){
								this.treatmentsession.assessment[section].formElements.forEach(markingElement=>{
									if(markingElement.value){
                                        //console.log('marked something', markingElement)
										hasMarkedSomething = true;
									} else {
                                        //console.log('hasnt marked something', markingElement)
										hasntMarkedSomething = true;
									}
								});
							}
						};
					}

					if(hasMarkedSomething){
						if(hasntMarkedSomething) {
							this.$store.commit("openDialog", {
								title: this.$t("finished-marking-this-session-question"),
								htmlContent: `<p>${this.$t("have-you-provided-all-marking-information-for-the-treatment-session-question")}</p><p>${this.$t("clicking-yes-will-lock-this-treatment-session-and-remove-it-from-your-list-of-treatment-sessions-awaiting-marks")}</p>`,
								cancelBtn: true,
								cancelBtnText: this.$t("no"),
								confirmBtnText: this.$t("yes"),
								confirmCallBack: function() {
									self.treatmentsession.isLocked = true;
									self.saveTreatmentSession();
								},
								cancelCallBack: function() {
									self.treatmentsession.isLocked = false;
									self.saveTreatmentSession();
								}
							});
						} else {
							this.treatmentsession.isLocked = true;
							this.saveTreatmentSession();
						}
					} else {
						this.saveTreatmentSession();
					}
				} else if(this.$config.getValue('markingvalidation') == 'remind'){
					if(this.validateMarking()){
						this.treatmentsession.isLocked = true;
						this.saveTreatmentSession();
					} else {
						this.$store.commit("openDialog", {
							title: this.$t("marking-incomplete-exclamation"),
							content: this.$t("there-are-incomplete-marking-fields-do-you-wish-to-save-anyway-question"),
							cancelBtn: true,
							cancelBtnText: this.$t("check-marking"),
							confirmBtnText: this.$t("save-anyway"),
							confirmCallBack: function() {
								self.saveTreatmentSession();
							},
							cancelCallBack: function() {
							}
						});
					}
				}

			} else {
				this.saveTreatmentSession();
			}

		},
		saveTreatmentSession(callback) {
			// a second tap while saving would create the session twice
			if (this.savingSession) {
				return;
			}
			this.savingSession = true;
			this.$store.commit("showBlockingSpinner");
			this.treatmentsession.treatmentTimestamp = this.$datetime.getTimeStampFromDateTimeField(
				this.treatmentsession.datetime
			);
			this.$cfetch(`${this.$settings.APIBaseURL}/treatmentsessions/save`, {
				method: "post",
				data: {
					treatmentsession: this.treatmentsession,
					markedByUser: this.markedByUser,
					markedByPassword: this.markedByPassword
				},
				retryIfOffline: true
			})
				.then(json => {
					this.$store.commit("hideBlockingSpinner");
					if (json.status === "SUCCESS") {
						if(callback){
							callback();
						} else {
							const navigate = () => {
								if (
									!this.treatmentsession.isLocked &&
									!this.treatmentsession.hasPatientFeedback &&
									this.treatmentsession.typeId == 1 &&
									this.treatmentsession.patientAttendance == 1 &&
									!this.$UserSession.hasPriv("TREATMENTSESSION_MANAGE") &&
									(this.$config.getValue("requestpatientfeedback") || this.$config.getValue("generatefeedbacktoken"))
								) {
									this.$router.replace(`/clinical/patientfeedback/${json.id}`);
								} else {
									this.$router.replace(`/clinical`);
								}
							};
							const triggered = this.checkCauseForConcernCriteria(navigate);
							if (!triggered) navigate();
						}
					} else {
						this.savingSession = false;
						this.$showApiError({ data: json }, { fallback: "unable-to-save-treatment-session" });
					}
				})
				.catch(error => {
					this.$store.commit("hideBlockingSpinner");
					if(error.timedOut && error.queuedForRetry) {
						// the save is queued and App.vue retries it, so don't let it be submitted again from here
						this.$store.commit("openDialog", {
							title: this.$t("application-offline"),
							content: this.$t("treatment-session-queued-for-retry"),
							confirmCallBack: () => this.$router.replace(`/clinical`)
						});
						return;
					}
					this.savingSession = false;
					this.$showApiError(error, { fallback: "unable-to-save-treatment-session" });
				});
		},
		iterateFormElements(formElements, procedure=null) {
			formElements.forEach(formElement => {
				formElement.display = true;
                formElement.requiredIncomplete = false;
				/*
				if(!('id' in formElement)){
					formElement.id = (Date.now() + 100000) + Math.floor(Math.random() * 100000);
				}
				*/
				formElement.elementid = `${Date.now() +
					100000 +
					Math.floor(Math.random() * 100000)}_${formElement.id}`;
				if (formElement.type == "multiselect") {
					if (!("value" in formElement)) {
						formElement.value = {
							empty: false
						};
					} else if (!formElement.value) {
						formElement.value = {
							empty: false
						};
					} else if (typeof formElement.value != 'object') {
						// If a select is changed to a multiselect after data is stored, this will prevent the old data from breaking the UI
						var newvalue = {
							empty: false
						};
						newvalue[formElement.value] = true
						formElement.value = newvalue;
					}
					if (!formElement.values) {
						console.error("multiselect formElement missing 'values':", formElement.id, formElement.name);
						formElement.values = [];
					}
					formElement.values.forEach(item => {
						if (!(item.value in formElement.value)) {
							formElement.value[item.value] = false;
						}
					});
				} else if (formElement.type == "toothmap") {
					formElement.showPrimary = false;
					if (!("value" in formElement)) {
						formElement.value = {
							empty: false
						};
					} else if (!formElement.value) {
						formElement.value = {
							empty: false
						};
					}
					// Empty values for adult teeth selections
					for (var i = 0; i <= 32; i++) {
						if (!(i in formElement.value)) {
							formElement.value[i] = false;
						}
					}
					// Empty values for primary teeth selections
					for (i = 0; i <= 20; i++) {
						if (!(`pri_${i}` in formElement.value)) {
							formElement.value[`pri_${i}`] = false;
						}
						if (formElement.value[`pri_${i}`] == true) {
							formElement.showPrimary = true;
						}
					}
				} else if (formElement.type == "radiographs") {
					let newvalue = [];
					formElement.types.forEach(type => {
						// Are there any settings in the saved procedure?
						// IF so, we need to reformat the for the vue code
						newvalue = [];
						if ("value" in formElement) {
							if (typeof formElement.value == "object") {
								if (`${type.name}_qty` in formElement.value) {
									for (
										let i = 1;
										i <= formElement.value[`${type.name}_qty`];
										i++
									) {
										newvalue.push(
											formElement.value[`${type.name}_auditboxes`][i]
										);
									}
								}
							}
						}
						if (!("value" in type)) {
							type.value = newvalue;
						} else if (!formElement.value) {
							type.value = newvalue;
						}
					});
				} else if (formElement.type == "text") {
					formElement.autosize = false;
					if (!("value" in formElement)) {
						formElement.value = "";
					}
				} else if (formElement.type == "textarea") {
					formElement.autosize = false;
					if (!("value" in formElement)) {
						formElement.value = "";
					}
				} else if (formElement.type == "multiplier") {
					if (!("value" in formElement)) {
						formElement.value = [];
					} else if (!formElement.value) {
						formElement.value = [];
					} else if (formElement.value === "0") {
						formElement.value = [];
					} else {
						formElement.value.forEach(value => {
							this.iterateFormElements(value.formElements, procedure);
						});
					}
				} else if (!("value" in formElement)) {
					formElement.value = false;
				}
				if (procedure && formElement.type == "flag" && formElement.value == true) {
					procedure.flagged = true;
				}
				if ("formElements" in formElement) {
					this.iterateFormElements(formElement.formElements, procedure);
				}
			});
		},
		checkParticipationType(){
			if(this.$config.getValue("livepatientconfirmdialog")){
				if(this.participationType){
					if(this.participationType.haspatient == 1){
						const self = this;
						var htmlContent = '<ul style="text-align: left">\
							<li>' + this.$t('ts-live-treating_person') + '</li>\
							<li>' + this.$t('ts-live-noassist') + '</li>\
							<li>' + this.$t('ts-live-disclinary') + '</li>\
						</ul>';
						this.$store.commit("openDialog", {
							title: this.$t("live-patient"),
							htmlContent: htmlContent,
							confirmCallBack: function() {
							} ,
							cancelBtn: true ,
							cancelCallBack: function() {
								self.treatmentsession.typeId = null;
							}
						});
					}
				}
			}
		},
		checkRequired() {
			//this.$log.info.call(this, "Checking required fields");
			this.treatmentsession.procedures.forEach(procedure => {
				if ("validation" in procedure) {
					if (procedure.validation) {
						let validated = false;
						let allvalidated = true;
						procedure.validation.forEach(validationitem => {
							validated = false;
							// Does this validation criteria pass?
							procedure.formElements.forEach(formElement => {
								return validationitem.forEach(fieldname => {
									if (formElement.name == fieldname) {
										if (typeof formElement.value == "object") {
											for (var option in formElement.value) {
												if (formElement.value[option]) {
													validated = true;
													return true;
												}
											}
										} else {
											if (formElement.value) {
												validated = true;
												return true;
											}
										}
									}
								});
							});
							// Highlight the fields, check all validated
							procedure.formElements.forEach(formElement => {
								validationitem.forEach(fieldname => {
									if (formElement.name == fieldname) {
										formElement.requiredIncomplete = !validated;
										if(!validated && formElement.display){
											allvalidated = false;
										}
									}
								});
							});
						});
						procedure.incomplete = !allvalidated;
					}
				}
			});
		},
		validateDependencies() {
			var formElementValues = {};
			var dependencies = {};

			let passed = true;
			let grouppassed = false;

			// build a lookup table of dependencies for procedures
			this.treatmentsession.procedures.forEach(procedure => {
				formElementValues = {};
				dependencies = {};
				procedure.formElements.forEach((formElement, index) => {
					// Also, while we're here, check for custom events
					if('customevent' in formElement && formElement['customevent']){
						eval(formElement['customevent']);
					}

					// Standard dependencies
					formElementValues[formElement.name] = {
						index: index,
						value: formElement.value
					};

					if ("dependencies" in formElement) {
						if (formElement.dependencies) {
							dependencies[index] = formElement.dependencies;
							formElement.display = false;
						}
					}
				});

				for (let di in dependencies) {
					passed = true;
					grouppassed = false;
					dependencies[di].forEach(andgroup => {
						for (let orgroup in andgroup) {
							for (let key in andgroup[orgroup]) {
								if(key in formElementValues){
									if(typeof formElementValues[key].value == 'object'){
										if(andgroup[orgroup][key] in formElementValues[key].value && formElementValues[key].value[andgroup[orgroup][key]] == true){
											grouppassed = true;
										}
									} else {
										if (formElementValues[key].value == andgroup[orgroup][key]) {
											grouppassed = true;
										}
									}
								}
							}
						}
						if (!grouppassed) {
							passed = false;
						}
					});
					if (passed) {
						procedure.formElements[di].display = true;
					} else {
						procedure.formElements[di].display = false;
						this.nullifyFormElement(procedure.formElements[di]);
						this.nullifyFormElementChildren(procedure.formElements[di]);
					}
				}
			});

			passed = true;
			grouppassed = false;
			// build a lookup table of dependencies for marking
			for (var sectionid in this.treatmentsession.assessment) {
				var section = this.treatmentsession.assessment[sectionid];
				formElementValues = {};
				dependencies = {};
				section.formElements.forEach((formElement, index) => {
					formElementValues[formElement.name] = {
						index: index,
						value: formElement.value
					};
					if ("dependencies" in formElement) {
						if (formElement.dependencies) {
							dependencies[index] = formElement.dependencies;
							formElement.display = false;
						}
					}
				});

				for (let di in dependencies) {
					passed = true;
					grouppassed = false;
					dependencies[di].forEach(andgroup => {
						for (let orgroup in andgroup) {
							for (let key in andgroup[orgroup]) {
								if(key in formElementValues){
									if(formElementValues[key].value){
										if(typeof formElementValues[key].value == 'object'){
											if(andgroup[orgroup][key] in formElementValues[key].value && formElementValues[key].value[andgroup[orgroup][key]] == true){
												grouppassed = true;
											}
										} else {
											if (formElementValues[key].value == andgroup[orgroup][key]) {
												grouppassed = true;
											}
										}
									}
								}
							}
						}
						if (!grouppassed) {
							passed = false;
						}
					});
					if (passed) {
						section.formElements[di].display = true;
					} else {
						section.formElements[di].display = false;
						this.nullifyFormElement(section.formElements[di]);
						this.nullifyFormElementChildren(section.formElements[di]);
					}
				}
			}
		},
		validateMarking: function() {
			var validated = true;
			for (let section in this.treatmentsession.assessment) {
				if('formElements' in this.treatmentsession.assessment[section]){
					this.treatmentsession.assessment[section].formElements.forEach(markingElement=>{
						if(markingElement.display && markingElement.required){
						 	if(!markingElement.value && markingElement.value !== 0 && markingElement.value !== '0'){
								markingElement.requiredIncomplete = true;
								validated = false;
							} else {
								markingElement.requiredIncomplete = false;
							}
						}
					});
				}
			};
			// Validate the procedure marking
			this.treatmentsession.procedures.forEach(procedure => {
				if('marking' in procedure && 'formElements' in procedure.marking){
					procedure.marking.formElements.forEach(markingElement=>{
						if(markingElement.display && markingElement.required){
						 	if(!markingElement.value && markingElement.value !== 0 && markingElement.value !== '0'){
								markingElement.requiredIncomplete = true;
								validated = false;
							} else {
								markingElement.requiredIncomplete = false;
							}
						}
					});
				}
			});
			/*
				if(FormBuilder.assessmentElements[i].settings.markingsets && FormBuilder.assessmentElements[i].settings.markingsets.indexOf(UIView.markingcriteria) != -1) {
					if(FormBuilder.assessmentElements[i].settings.id != 'isLocked' && FormBuilder.assessmentElements[i].settings.id != 'studentFeedback') {
						if(FormBuilder.assessmentElements[i].settings.required == true){
							 if(!FormBuilder.assessmentElements[i].validate()){
								validated = false;
							 }
						}
					}
				}
			}
			*/
			return validated;
		},
		nullifyFormElementChildren(parentFormElement) {
			if ("formElements" in parentFormElement) {
				parentFormElement.formElements.forEach(formElement => {
					this.nullifyFormElement(formElement);
					this.nullifyFormElementChildren(formElement);
				});
			}
		},
		nullifyFormElement(formElement){
			if(formElement.value){
				if(typeof formElement.value == 'object'){
					for (let key in formElement.value){
						formElement.value[key] = false;
					}
				} else {
					formElement.value = null;
				}
			}
		},
		addMarking() {
			// Add the formfields for the assessment screen to the treatmentsession object
			var sections = ["markingmain", "markingsummary", "markingsupp"];
			if (!("assessmentfields" in this.treatmentsession)) {
				this.treatmentsession.assessment = {
					markingmain: {
						display: false,
						formElements: []
					},
					markingsummary: {
						display: false,
						formElements: []
					},
					markingsupp: {
						display: false,
						formElements: []
					}


				};
			}
			// Iterate over the configured assessment sections. Add them into the treatment session.
			sections.forEach(section => {
				if (section in this.$config.getValue("assessmentfields")) {
					var fields = JSON.parse(
						JSON.stringify(this.$config.getValue("assessmentfields")[section])
					);
					fields.forEach(formElement => {
						if (!("value" in formElement)) {
							if (formElement.type == "textarea" || formElement.type == "integer") {
								formElement.value = "";
							} else {
								formElement.value = false;
							}
						}
						formElement.elementid = formElement.id;
						formElement.display = true;
						formElement.requiredIncomplete = false;
						// Is there a saved value in the loaded treatment session for this item?
						// "integer" fields are persisted in the "textarea" bucket, so look there instead.
						var bucket = formElement.type == "integer" ? "textarea" : formElement.type;
						if (bucket in this.treatmentsession) {
							if (formElement.id in this.treatmentsession[bucket]) {
								formElement.value = this.treatmentsession[bucket][
									formElement.id
								];
							}
						}
						this.treatmentsession.assessment[section].formElements.push(formElement);
					});
				}
			});
			// Add the procedure marking
			this.treatmentsession.procedures.forEach(procedure => {
				var marks = JSON.parse(JSON.stringify(procedure.marking));
				procedure.marking = {
					formElements: []
				};
				if (procedure.markingset) {
					var proceduremarking = this.$config.getValue("proceduremarking");
					if (proceduremarking) {
						proceduremarking.fields.forEach(formElement => {
							if ("markingsets" in formElement) {
								formElement.display = false;
								formElement.requiredIncomplete = false;
								formElement.markingsets.forEach(markingset => {
									if (procedure.markingset == markingset) {
										var addFormElement = JSON.parse(
											JSON.stringify(formElement)
										);
										addFormElement.display = true;
										addFormElement.value = null;
										if (addFormElement.id in marks) {
											addFormElement.value = marks[addFormElement.id].value;
										}
										procedure.marking.formElements.push(addFormElement);
									}
								});
							}
						});
					}
				}
			});
		},
		configureMarking() {
			this.$log.info.call(this, "Reconfiguring marking");

			var markingcriteria = null;
			if (this.$config.getValue("participationTypes")) {
				this.$config.getValue("participationTypes").forEach(participationType => {
					if (participationType.id == this.treatmentsession.typeId) {
						markingcriteria = participationType.markingcriteria;
					}
				});
			}

			for (let section in this.treatmentsession.assessment) {
				this.treatmentsession.assessment[section].display = false;
				this.treatmentsession.assessment[section].formElements.forEach(formElement => {
					if ("markingsets" in formElement) {
						formElement.display = false;
						formElement.markingsets.forEach(markingset => {
							if (markingcriteria == markingset) {
								this.treatmentsession.assessment[section].display = true;
								formElement.display = true;
							}
							/*
							if(markingset == 'all'){
								formElement.display = true;
							}
							*/
						});
					} else {
						this.treatmentsession.assessment[section].display = true;
						formElement.display = true;
					}
					if("programme" in formElement) {
						formElement.display = false;
						this.$users.getUserById.call(this, this.treatmentsession.student).then(student => {
							formElement.programme.forEach(programme => {
								if (student.programme == programme) {
									if("year" in formElement) {
										formElement.year.forEach(year => {
											if (student.year == year) {
												this.treatmentsession.assessment[section].display = true;
												formElement.display = true;
											}
										});
									} else {
										this.treatmentsession.assessment[section].display = true;
										formElement.display = true;
									}
								}
							});
						});
					}
				});
			}
		},
		selectStudent() {
			const self = this;
			this.$users.getStudents.call(this).then(students => {
				this.$store.commit("openModal", {
					type: "selector",
					title: this.$t("select-a-student"),
					selectCallback: function(selection) {
						self.treatmentsession.student_displayname = selection.displayName;
						self.treatmentsession.student = selection.id;
						self.$store.commit("closeModal");
						self.$refs.teacher.focus();

						// nuke clinical case
						self.treatmentsession.clinicalcase = {
							id: null,
							patientref: null,
							complete: false,
							status: null,
							recall: null,
							recallperiod: null,
							transferred: null,
							transferredtouser: null,
							transferredtouserDisplayName: null
						};
						self.treatmentsession.patient = null;
					},
					selected: [
						{
							id: this.treatmentsession.student
						}
					],
					data: students,
					captionKey: "displayName",
                    subcaptionKey: "studentid",
					studentSearchFilters: true
				});
			});
		},
		selectTeacher() {
			const self = this;
			this.$users.getTeachers.call(this).then(teachers => {
				const processedTeachers = teachers.map(teacher => {
				if (teacher.status && teacher.status.toLowerCase() === "closed") {
					teacher.subcaption = this.$t("(Closed)");
				}
				return teacher;
				});
				this.$store.commit("openModal", {
				type: "selector",
				title: this.$t("select-a-teacher"),
				selectCallback: function(selection) {
					self.treatmentsession.teacher_displayname = selection.displayName;
					self.treatmentsession.teacher = selection.id;
					self.$store.commit("closeModal");
					self.$refs.specialty.focus();
				},
				selected: [
					{
					id: this.treatmentsession.teacher
					}
				],
				data: processedTeachers,
				captionKey: "displayName",
				subcaptionKey: "subcaption"
				});
			});
		},

		tryGetFeedbackToken() {
			if(
				this.$config.getValue("generatefeedbacktoken") // should generate feedback token if pass other criteria
				&& this.participationType
				&& this.participationType.haspatient == 1
				&& this.treatmentsession.patientAttendance == "1" // patient attended
				&& !this.treatmentsession.feedbacktoken // no feedback token
			) {
				//this.$store.commit("showBlockingSpinner");
				// fetch one from api/3.0/treatmentsessions/generatefeedbacktoken, and set it on treatmentsession.feedbacktoken
				this.treatmentsession.feedbacktoken = '...';
				return this.$cfetch(`${this.$settings.APIBaseURL}/treatmentsessions/generatefeedbacktoken`)
				.then((json) => {
					if (json.token) {
						this.treatmentsession.feedbacktoken = json.token;
						//this.$store.commit("hideBlockingSpinner");
					}
				})
				.catch((error) => {
					this.$log.error.call(this, error);
					this.treatmentsession.feedbacktoken = null;
				});
			}
			return;
		},
		checkCauseForConcernCriteria(afterDismiss = null) {
			const assessment = this.treatmentsession.assessment;
			if (!assessment) return false;

			let triggered = false;

			for (const sectionKey in assessment) {
				const section = assessment[sectionKey];
				if (!section || !section.formElements) continue;

				const gradeElements = section.formElements.filter(el => el.type === 'grade' && el.display);

				section.formElements.forEach(el => {
					if (el.type !== 'bool' || !el.autoTrigger || !el.display) return;
					if (el.value) return;

					const { conditions, modal: autoModal } = el.autoTrigger;
					if (!conditions || !conditions.length) return;

					const conditionMet = conditions.some(condition => {
						// eslint-disable-next-line eqeqeq
						const count = gradeElements.filter(g => g.value == condition.value).length;
						return count >= condition.min;
					});

					if (!conditionMet) return;

					const modal = autoModal || el.modal;
					if (!modal) return;

					triggered = true;

					let htmlContent = modal.content ? '<p>' + modal.content + '</p>' : undefined;
					if (modal.form_link) {
						const path = '/forms/' + modal.form_link + '/responses/new/stage/' + modal.form_stage;
						const href = this.$router.resolve(path).href;
						htmlContent = (htmlContent || '')
							+ '<p><a href="' + href + '" target="_blank" rel="noopener noreferrer">' + (modal.form_label || 'Open form') + ' ↗</a></p>';
					}
					this.$store.commit('openDialog', {
						title: modal.title || '',
						htmlContent: htmlContent,
						size: modal.size || undefined,
						cancelBtn: false,
						confirmCallBack: afterDismiss || undefined
					});
				});
			}

			return triggered;
		},
		triggerElementModal(formElement, value) {
			const modal = formElement.modal;
			if (!modal) return;
			const self = this;
			if (modal.form_id) {
				this.handleShowFormFill({ formId: modal.form_id, formElement, value });
				return;
			}
			if (modal.formresponse_id) {
				this.handleShowFormResponse({ formresponseId: modal.formresponse_id, formElement, value });
				return;
			}
			let htmlContent = modal.content ? '<p>' + modal.content + '</p>' : undefined;
			if (modal.form_link) {
				const path = '/forms/' + modal.form_link + '/responses/new/stage/' + modal.form_stage;
				const href = self.$router.resolve(path).href;
				htmlContent = (modal.content ? '<p>' + modal.content + '</p>' : '')
					+ '<p><a href="' + href + '" target="_blank" rel="noopener noreferrer">' + (modal.form_label || 'Open form') + ' ↗</a></p>';
			}
			this.$store.commit('openDialog', {
				title: modal.title || '',
				htmlContent: htmlContent,
				size: modal.size || undefined,
				cancelBtn: modal.type === 'confirm',
				confirmBtnText: modal.confirmBtnText || undefined,
				cancelBtnText: modal.cancelBtnText || undefined,
				confirmCallBack: function() {
					self.$set(formElement, 'value', value);
				}
			});
		},
		trySelectClinicalCase() {
			if (this.$config.getValue("trackclinicalcases")) {
				this.$cfetch
					.call(
						this,
						`${this.$settings.APIBaseURL}/treatmentsessions/getactiveclinicalcases`,
						{
							method: "post",
							data: {
								id: this.$route.params.treatmentsessionid,
								studentId: this.treatmentsession.student
							}
						}
					)
					.then(json => {
						if (json.status === "SUCCESS") {
							const self = this;
							this.$store.commit("openModal", {
								type: "selector",
								title: this.$t("select-clinical-case"),
								selectCallback: function(selection) {
									//self.treatmentsession.clinicalcase.id = selection.id;
									self.treatmentsession.patient = selection.patientref;
									var clinicalcase = JSON.parse(JSON.stringify(selection));
									clinicalcase.transferredtouserDisplayName = "";
									if (clinicalcase.transferredtouser) {
										this.$users.getDisplayName
											.call(this, clinicalcase.transferredtouser)
											.then(result => {
												clinicalcase.transferredtouserDisplayName = result;
											});
									}
									self.treatmentsession.clinicalcase = clinicalcase;
									self.$store.commit("closeModal");
									self.$refs.patientattendance.focus();
								},
								selected: [
									{
										id: self.treatmentsession.clinicalcase.id
									}
								],
								data: json.clinicalcases,
								captionKey: "patientref",
								newBtn: true,
								newValueMaxLength: this.$config.getValue("patientrefmaxlen"),
								newValueMinLength: this.$config.getValue("patientrefminlen"),
								newCallback: function(patientId) {
									self.treatmentsession.patient = patientId;
									self.treatmentsession.clinicalcase = {
										id: null,
										user_id: self.treatmentsession.student,
										patientref: patientId,
										complete: false,
										status: null,
										recall: null,
										recallperiod: null,
										transferred: null,
										transferredtouser: null,
										transferredtouserDisplayName: ""
									};
								},
								placeholder: this.$t("patient-reference")
							});
						} else {
							this.$log.error.call(this, "Could not fetch treatment session");
						}
					})
					.catch(error => {
						this.$log.error.call(this, error);
					});
			}
		},
		selectTransferredTo() {
			const self = this;
			if (this.treatmentsession.clinicalcase.transferred == "Staff") {
				this.$users.getTeachers.call(this).then(teachers => {
					this.$store.commit("openModal", {
						type: "selector",
						title: this.$t("select-a-teacher"),
						selectCallback: function(selection) {
							self.treatmentsession.clinicalcase.transferredtouserDisplayName =
								selection.displayName;
							self.treatmentsession.clinicalcase.transferredtouser = selection.id;
							self.$store.commit("closeModal");
						},
						selected: [
							{
								id: self.treatmentsession.clinicalcase.transferredtouser
							}
						],
						data: teachers,
						captionKey: "displayName"
					});
				});
			} else {
				this.$users.getStudents.call(this).then(students => {
					this.$store.commit("openModal", {
						type: "selector",
						title: this.$t("select-a-student"),
						selectCallback: function(selection) {
							self.treatmentsession.clinicalcase.transferredtouserDisplayName =
								selection.displayName;
							self.treatmentsession.clinicalcase.transferredtouser = selection.id;
							self.$store.commit("closeModal");
						},
						selected: [
							{
								id: self.treatmentsession.clinicalcase.transferredtouser
							}
						],
						data: students,
						captionKey: "displayName",
                        subcaptionKey: "studentid",
						studentSearchFilters: true
					});
				});
			}
		},
		addProcedureById(id) {
			this.formsettings.sections.forEach(section => {
				section.procedures.forEach(procedure => {
					if(procedure.id == id){
						this.addProcedure(procedure);
					}
				});
			});
		},
		addProcedure(procedure) {
			// Prepare the new procedure
			const newid = Date.now();
			// Object assign does not work to copy Vue objects, as some of it is copied by reference.
			// var procedureadd = Object.assign({}, procedurebtn);
			const procedureadd = JSON.parse(JSON.stringify(procedure));
			procedureadd.treatmentsessionprocedureid = newid;
			procedureadd.incomplete = false;
			procedureadd.flagged = false;

			// Add the marking
			procedureadd.marking = {
				formElements: []
			};
			if (procedureadd.markingset) {
				var proceduremarking = this.$config.getValue("proceduremarking");
				if (proceduremarking) {
					proceduremarking.fields.forEach(formElement => {
						if ("markingsets" in formElement) {
							formElement.display = false;
							formElement.markingsets.forEach(markingset => {
								if (procedureadd.markingset == markingset) {
									var addFormElement = JSON.parse(JSON.stringify(formElement));
									addFormElement.display = true;
									addFormElement.value = null;
									procedureadd.marking.formElements.push(addFormElement);
								}
							});
						}
					});
				}
			}

			// We're not doing this here now. We're preparing the settings in advance after API load.
			// this.iterateFormElements(procedureadd.formElements);
			// procedureadd.isSelected = false;

			// Add the history
			procedureadd.history = null;
			this.getProcedureHistory(procedureadd , this.treatmentsession.student);

			this.treatmentsession.procedures.push(procedureadd);
			this.procedureselector = {};
			this.proceduresearch = false;
			this.proceduresearchstring = "";
			this.toggleProcedureSelect(procedureadd);
		},
		removeProcedure(index, procedure) {
			var self = this;
			this.$store.commit("openDialog", {
				cancelBtn: true,
				title: this.$t("remove-procedure"),
				content: this.$t("remove-procedure-question", { procedure: procedure.caption }),
				confirmCallBack: function() {
					self.treatmentsession.procedures.splice(index, 1);
				}
			});
		},
		getProcedureHistory: function(procedure, student_id){
			this.$cfetch(`${this.$settings.APIBaseURL}/treatmentsessions/getprocedurehistory`, {
				cache: false,
				data: {
					"userid": student_id ,
					"procedureid": procedure.id
				},
				method: "post",
			})
			.then((json) => {
				if (json.status === "SUCCESS") {
					this.$log.info.call(this, "History loaded");
					procedure.history = json.history;
				} else {
					this.$log.error.call(this, "Could not fetch procedure history");
				}
			})
			.catch((error) => {
				this.$log.error.call(this, error);
			});
		},
		showProcedureHistory: function(procedure){
			if(procedure.history){
				this.procedurehistory = procedure.history;
			}
		},
		procedureSearchResults: function() {
			var results = {};
			if (this.proceduresearchstring) {
				this.formsettings.sections.forEach(section => {
					section.procedures.forEach(procedure => {
						if (
							procedure.caption
								.toLowerCase()
								.includes(this.proceduresearchstring.toLowerCase())
						) {
							results[procedure.id] = procedure;
						} else if (
							"keywords" in procedure &&
							typeof procedure.keywords == "string"
						) {
							if (
								procedure.keywords
									.toLowerCase()
									.includes(this.proceduresearchstring.toLowerCase())
							) {
								results[procedure.id] = procedure;
							}
						}
					});
				});
			}
			return results;
		},
		clearProcedureSelect(){
			this.treatmentsession.procedures.forEach(item => {
				item.isSelected = false;
			});
		},
		toggleProcedureSelect(procedure) {
			this.treatmentsession.procedures.forEach(item => {
				if (item.treatmentsessionprocedureid == procedure.treatmentsessionprocedureid) {
					// item.isSelected = true;
					item.isSelected = !item.isSelected;
					if('formElements' in item){
						item.formElements.forEach(formElement => {
							if (formElement.type == "textarea") {
								formElement.autosize = true;
							}
						});
					}
				} else {
					item.isSelected = false;
				}
			});
		},
		prev() {
			if (this.screen == 0) {
				this.tryClose();
			} else {

				if(this.patientDidNotAttend) {
					// skip procedures
					this.screen = 0;
				} else {
					this.screen--;
				}

				this.proceduresParticipationTypes('prev');

				/*
				let content = document.querySelector(".clinical-content");
				content.scrollLeft = (window.innerWidth - 50) * this.screen;
				*/
				const content = document.querySelector(".clinical-content");
				let currentpos = content.scrollLeft;
				const destpos = content.offsetWidth * this.screen;
				const increment = content.offsetWidth / 20;
				const animation = window.setInterval(() => {
					currentpos -= increment;
					if (currentpos <= destpos) {
						currentpos = destpos;
						window.clearInterval(animation);
					}
					content.scrollLeft = currentpos;
				}, 10);
			}
		},
		next() {
			if (this.screen == 2) {
				this.trySaveTreatmentSession();
			} else {

				this.configureMarking();

				if(this.patientDidNotAttend) {
					// skip procedures
					this.treatmentsession.procedures = [];
					this.screen = 2;
				} else {
					this.screen++;
				}

				this.proceduresParticipationTypes('next');

				const content = document.querySelector(".clinical-content");
				let currentpos = content.scrollLeft;
				const destpos = content.offsetWidth * this.screen;
				const increment = content.offsetWidth / 20;
				const animation = window.setInterval(() => {
					currentpos += increment;
					if (currentpos >= destpos) {
						currentpos = destpos;
						window.clearInterval(animation);
					}
					content.scrollLeft = currentpos;
				}, 10);

			}
		},
		resizeHandler() {
			const content = document.querySelector(".clinical-content");
			const destpos = content.offsetWidth * this.screen;
			content.scrollLeft = destpos;
		},
		proceduresParticipationTypes(nextprev) {

			var participationType = this.participationType;
			if(participationType){
				if(participationType.id == this.treatmentsession.typeId) {
					if(participationType.skipprocedures == 1) {
						if(nextprev == 'prev'){
                    		this.screen--;
						} else {
                    		this.screen++;
                    	}
                    }
                    if(participationType.allowaddprocedures == 0) {
                        this.allowaddprocedures = false;
                        // DANGER: this will DELETE anything already in the session on Load
                        // DO NOT DO THIS.
                        // this.treatmentsession.procedures = [];
                    } else {
                    	this.allowaddprocedures = true;
                    }

					if(!this.patientDidNotAttend) {
						if('defaultprocedures' in participationType){
							participationType.defaultprocedures.forEach(id => {
								var alreadyadded=false;
								this.treatmentsession.procedures.forEach(procedure => {
									if(procedure.id == id){
										alreadyadded=true;
									}
								});
								if(!alreadyadded){
									this.addProcedureById(id);
								}
							});
						}
                    }
				}
			}
		},
		removeRequiredField(field) {
    		let index = this.requiredFields.indexOf(field);
    		if (index !== -1) {
      		this.requiredFields.splice(index, 1); // move the splice() method to a method
    		}
		},
		tryClose() {
			if (this.numupdates > 1) {
				this.$store.commit("openDialog", {
					cancelBtn: true,
					title: this.$t("quit-without-saving-question"),
					content: this.$t("all-changes-will-be-lost"),
					confirmCallBack: function() {
						this.$router.replace("/clinical");
					}
				});
			} else {
				this.$router.replace("/clinical");
			}
		},
		snapListen() {
			// Listen for scroll events
			const self = this;
			const content = document.querySelector(".clinical-content");
			content.addEventListener(
				"scroll",
				() => {
					// Clear our timeout throughout the scroll
					window.clearTimeout(self.isScrolling);
					// Set a timeout to run after scrolling ends
					self.isScrolling = setTimeout(() => {
						self.snap();
					}, 200);
				},
				false
			);
		},
		snap() {
			const content = document.querySelector(".clinical-content");
			const width = content.offsetWidth;
			const checkpos = content.scrollLeft / width;
			this.screen = Math.round(checkpos);
			if (checkpos != this.screen) {
				content.scrollLeft = Math.round(checkpos) * width;
			}
		},
		toggleComments: function() {
			this.$store.state.treatmentSessionComments.showModal = !this.$store.state.treatmentSessionComments.showModal;
		},
		toggleSelectCompetencyTests: function() {
			this.$store.state.treatmentSessionSelectCompetencyTests.showModal = !this.$store.state
				.treatmentSessionSelectCompetencyTests.showModal;
		},
		markCompetencyTest: function(competencytest) {
			this.competencytestmarking = competencytest;
			this.showcompetencytestmarking = true;
		},
		hideCompetencyTestMarking: function() {
			this.showcompetencytestmarking = false;
		},
		showProcedureInfo(procedure){
			var html = '';
			if(procedure.information){
				html += procedure.information;
			}
			if(procedure.validationinformation){
				html += '<h3>' + this.$t('requirements') + '</h3>';
				html += procedure.validationinformation;
			}
			this.showInfo(procedure.caption, html);
		},
		showInfo: function(caption, information) {
			this.$store.commit("openDialog", {
				cancelBtn: false,
				title: caption,
				htmlContent: information,
				size: "lg"
			});
		},
		tryLogin: function(){
			this.loginFailed = false;
			this.$cfetch(`${this.$settings.APIBaseURL}/login`, {
				authed: false,
				method: "post",
				ignoreauthfailed: true,
				data: {
					username: this.tryMarkedByUser,
					password: this.tryMarkedByPassword,
				},
			})
				.then((json) => {
					if (json.status === "SUCCESS") {
						this.loginFailed = false;
						this.markedByUser = this.tryMarkedByUser;
						this.markedByPassword = this.tryMarkedByPassword;
					} else {
						this.loginFailed = true;
					}
				})
				.catch((error) => {
					this.loginFailed = true;
					this.$log.error.call(this, error);
				});
		},
		logout: function(){
			this.markedByUser = null;
			this.markedByPassword = null;
		},

        validationHandler(value){
            if(this.$config.getValue('cansavewithoutvalidation')) this.field_validation = true;

            else this.field_validation = value;

        },
        handleShowFormFill({ formId, formElement, value }) {
            this.formFillModal = { show: true, formId, formElement, value };
        },
        confirmFormFillModal() {
            if (this.formFillModal.formElement) {
                this.$set(this.formFillModal.formElement, 'value', this.formFillModal.value);
            }
            this.formFillModal = { show: false, formId: null, formElement: null, value: null };
        },
        cancelFormFillModal() {
            this.formFillModal = { show: false, formId: null, formElement: null, value: null };
        },
        handleShowFormResponse({ formresponseId, formElement, value }) {
            this.formResponseModal = { show: true, formresponseId, formElement, value };
        },
        confirmFormResponseModal() {
            if (this.formResponseModal.formElement) {
                this.$set(this.formResponseModal.formElement, 'value', this.formResponseModal.value);
            }
            this.formResponseModal = { show: false, formresponseId: null, formElement: null, value: null };
        },
        cancelFormResponseModal() {
            this.formResponseModal = { show: false, formresponseId: null, formElement: null, value: null };
        }
	}
};
</script>
